/**
 * Formatted text: simple Markdown, and only this (docs/features/supplier-interface/28, ruling 27).
 *
 *  - `- ` or `* ` at the start of a line: a bullet. `1. ` or `1) `: a numbered item (the number typed is ignored).
 *  - `**bold**`, `*italic*` or `_italic_`; bold may hold italic and italic may hold bold, nothing nests deeper.
 *  - A line break is a line break; a blank line starts a new paragraph. Lists do not nest.
 *  - Nothing else is drawn: links, images, headings, tables, code and HTML stay as the characters typed. An
 *    unclosed marker is literal.
 *
 * The value is stored and carried as the text as written; this parser only turns it into blocks for a shell to draw
 * as elements (never as an HTML string). Pure: no DOM, no React, nothing web-only.
 */

/** The one-line hint under a formatted-text input (ruling 27's words). */
export const FORMATTED_TEXT_HINT = "Use - for bullets and **bold**";

export type FormattedInline =
  | { kind: "text"; text: string }
  | { kind: "bold"; children: FormattedInline[] }
  | { kind: "italic"; children: FormattedInline[] };

/** One line: a paragraph's lines are separate `FormattedLine`s, drawn with a break between. */
export type FormattedLine = FormattedInline[];

export type FormattedBlock =
  | { kind: "paragraph"; lines: FormattedLine[] }
  | { kind: "bullets"; items: FormattedLine[] }
  | { kind: "numbered"; items: FormattedLine[] };

const BULLET = /^\s*[-*]\s+(\S.*)$/;
const NUMBERED = /^\s*\d+[.)]\s+(\S.*)$/;

export function parseFormattedText(text: string): FormattedBlock[] {
  const blocks: FormattedBlock[] = [];
  let open: FormattedBlock | null = null;
  const add = (kind: FormattedBlock["kind"], line: string) => {
    const parsed = parseInline(line);
    if (open && open.kind === kind) {
      if (open.kind === "paragraph") open.lines.push(parsed);
      else open.items.push(parsed);
      return;
    }
    const block: FormattedBlock =
      kind === "paragraph"
        ? { kind, lines: [parsed] }
        : { kind, items: [parsed] };
    open = block;
    blocks.push(block);
  };
  for (const raw of text.replace(/\r\n?/g, "\n").split("\n")) {
    const line = raw.trim();
    if (line === "") {
      open = null;
      continue;
    }
    const bullet = BULLET.exec(raw);
    const numbered = bullet ? null : NUMBERED.exec(raw);
    if (bullet) add("bullets", bullet[1].trim());
    else if (numbered) add("numbered", numbered[1].trim());
    else add("paragraph", line);
  }
  return blocks;
}

/** The text with the markers taken out, on one line: a list cell, a notification, an aria-label. */
export function plainTextOf(blocks: readonly FormattedBlock[]): string {
  const inline = (nodes: readonly FormattedInline[]): string =>
    nodes
      .map((n) => (n.kind === "text" ? n.text : inline(n.children)))
      .join("");
  return blocks
    .map((block) =>
      block.kind === "paragraph"
        ? block.lines.map(inline).join(" ")
        : block.items.map(inline).join("; "),
    )
    .join(" ");
}

type Marker = "**" | "*" | "_";

interface Parsed {
  nodes: FormattedInline[];
  end: number;
  closed: boolean;
}

const MARKER = /[*_]/g;
const isSpace = (c: string | undefined) => c === undefined || /\s/.test(c);
const isWordChar = (c: string | undefined) =>
  c !== undefined && /[\p{L}\p{N}]/u.test(c);

/**
 * One line's bold and italic. Each marker is tried as an opener and kept only when it closes; one that does not is
 * literal text. A parse of (position, closer, what may nest) has one answer, so it is remembered: a line of
 * unclosed markers costs a pass per marker, not a pass per combination.
 */
function parseInline(line: string): FormattedInline[] {
  const memo = new Map<string, Parsed>();
  // A line of nothing but unclosed markers would still cost a pass per marker; past this many marker visits the rest
  // of the line is left as typed, so no input takes long and no character is lost.
  let budget = 20 * line.length + 1000;

  function parse(
    start: number,
    stop: Marker | null,
    allowBold: boolean,
    allowItalic: boolean,
  ): Parsed {
    const key = `${start}|${stop}|${allowBold}|${allowItalic}`;
    const known = stop ? memo.get(key) : undefined;
    if (known) return known;
    const nodes: FormattedInline[] = [];
    let literal = "";
    const flush = () => {
      if (literal) nodes.push({ kind: "text", text: literal });
      literal = "";
    };
    const done = (end: number, closed: boolean): Parsed => {
      flush();
      const result = { nodes, end, closed };
      if (stop) memo.set(key, result);
      return result;
    };
    let i = start;
    while (i < line.length) {
      const c = line[i];
      if (c !== "*" && c !== "_") {
        MARKER.lastIndex = i;
        const next = MARKER.exec(line)?.index ?? line.length;
        literal += line.slice(i, next);
        i = next;
        continue;
      }
      if ((budget -= 1) < 0) {
        literal += line.slice(i);
        return done(line.length, false);
      }
      const isDouble = line.startsWith("**", i);
      const opensBold =
        allowBold && isDouble && !isSpace(line[i + 2]) && stop !== "**";
      if (opensBold) {
        const inner = parse(i + 2, "**", false, allowItalic);
        if (inner.closed && inner.nodes.length > 0) {
          flush();
          nodes.push({ kind: "bold", children: inner.nodes });
          i = inner.end + 2;
          continue;
        }
        literal += "**";
        i += 2;
        continue;
      }
      if (stop === "**" && isDouble && i > start && !isSpace(line[i - 1]))
        return done(i, true);
      if (
        (stop === "*" || stop === "_") &&
        c === stop &&
        i > start &&
        !isSpace(line[i - 1]) &&
        (stop === "*" || !isWordChar(line[i + 1]))
      )
        return done(i, true);
      if (isDouble) {
        literal += "**";
        i += 2;
        continue;
      }
      const opensItalic =
        allowItalic &&
        (c === "*" || c === "_") &&
        !isSpace(line[i + 1]) &&
        (c === "*" || !isWordChar(line[i - 1]));
      if (opensItalic) {
        const inner = parse(i + 1, c as Marker, allowBold, false);
        if (inner.closed && inner.nodes.length > 0) {
          flush();
          nodes.push({ kind: "italic", children: inner.nodes });
          i = inner.end + 1;
          continue;
        }
      }
      literal += c;
      i += 1;
    }
    return done(i, false);
  }

  return parse(0, null, true, true).nodes;
}
